import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';
import { useAuth } from '@/hooks/useAuth';
import type { UserRole } from '@/domain/auth/access';
import { hasEssentialDemoScope } from '@/lib/business/essentialDemoScope';
import { hasStreetSalesScope } from '@/lib/business/streetSalesScope';

interface NavItem {
  path: string;
  label: string;
  icon: string;
  roles: UserRole[];
  b2bOnly?: boolean;
  module?: string;
  external?: boolean;
}

const allNavItems: NavItem[] = [
  { path: '/dashboard', label: 'Dashboard', icon: 'ri-dashboard-line', roles: ['super_admin', 'business_admin'] },
  { path: '/kitchen', label: 'Modo Cocina', icon: 'ri-restaurant-2-line', roles: ['super_admin', 'business_admin', 'supervisor'] },
  { path: '/orders/capture', label: 'Tomar Pedido', icon: 'ri-add-circle-line', roles: ['super_admin', 'business_admin', 'supervisor', 'employee'] },
  { path: '/orders/history', label: 'Historial y Caja', icon: 'ri-history-line', roles: ['super_admin', 'business_admin', 'supervisor', 'employee'] },
  { path: '/route-customers', label: 'Clientes de ruta', icon: 'ri-contacts-book-2-line', roles: ['supervisor', 'employee'], b2bOnly: true, module: 'b2b_customers' },
  { path: '/customers', label: 'Clientes', icon: 'ri-user-heart-line', roles: ['super_admin', 'business_admin'] },
  { path: '/products', label: 'Productos', icon: 'ri-store-2-line', roles: ['super_admin', 'business_admin', 'employee'] },
  { path: '/inventory', label: 'Inventario', icon: 'ri-archive-line', roles: ['business_admin', 'employee'] },
  { path: '/inventory?section=purchases', label: 'Compras y gastos', icon: 'ri-shopping-bag-3-line', roles: ['business_admin'], b2bOnly: true, module: 'purchases' },
  { path: '/business-sales?section=customers', label: 'Clientes empresariales', icon: 'ri-building-2-line', roles: ['super_admin', 'business_admin'], b2bOnly: true, module: 'b2b_customers' },
  { path: '/business-sales?section=quotes', label: 'Cotizaciones', icon: 'ri-file-paper-2-line', roles: ['super_admin', 'business_admin', 'employee'], b2bOnly: true, module: 'quotations' },
  { path: '/business-sales?section=scheduled', label: 'Pedidos programados', icon: 'ri-calendar-schedule-line', roles: ['super_admin', 'business_admin'], b2bOnly: true, module: 'scheduled_orders' },
  { path: '/business-sales?section=credit', label: 'Crédito y cobranza', icon: 'ri-bank-card-line', roles: ['super_admin', 'business_admin'], b2bOnly: true, module: 'credit_accounts' },
  { path: '/business-sales?section=delivery', label: 'Rutas y reparto', icon: 'ri-route-line', roles: ['super_admin', 'business_admin'], b2bOnly: true, module: 'delivery_zones' },
  { path: '/billing', label: 'Facturación', icon: 'ri-bill-line', roles: ['super_admin', 'business_admin'], b2bOnly: true, module: 'billing' },
  { path: '/reports', label: 'Reportes', icon: 'ri-bar-chart-box-line', roles: ['super_admin', 'business_admin'], module: 'reports' },
  { path: '/settings', label: 'Configuración', icon: 'ri-settings-3-line', roles: ['super_admin', 'business_admin', 'employee'] },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { brandLogo } = useTheme();
  const { user, logout } = useAuth();
  const essentialDemoScope = hasEssentialDemoScope(user);
  const streetSalesScope = hasStreetSalesScope(user);
  const b2bLabels: Record<string, string> = {
    '/orders/capture': 'Nueva venta',
    '/orders/history': 'Ventas y caja',
    '/products': 'Catálogo',
    '/inventory': 'Inventario',
  };

  const navItems = allNavItems.filter((item) => {
    if (!user) return false;
    if (essentialDemoScope && ['/inventory?section=purchases', '/billing'].includes(item.path)) return false;
    if (!item.roles.includes(user.role)) return false;
    if (item.b2bOnly && !user.isB2B) return false;
    if (user.isB2B && user.role === 'employee') {
      const employeePaths = streetSalesScope ? ['/orders/capture', '/orders/history', '/products', '/inventory', '/settings', '/business-sales?section=quotes'] : ['/orders/capture', '/orders/history', '/route-customers'];
      if (!employeePaths.includes(item.path)) return false;
      if (item.path === '/orders/capture') return user.activeModules.includes('orders');
      if (item.path === '/orders/history') return user.activeModules.includes('history');
      if (item.path === '/products' || item.path === '/inventory') return user.activeModules.includes('inventory');
      if (item.path === '/business-sales?section=quotes') return user.businessId === 'd014a201-ec56-41a5-8ffc-015348a4dcce';
      return true;
    }
    if (user.isB2B && item.path === '/customers') return false;
    if (item.module && !user.activeModules.includes(item.module)) return false;
    if (item.path === '/kitchen') {
      return user.activeModules.includes('kitchen');
    }
    if (item.path === '/inventory') return user.activeModules.includes('inventory');
    return true;
  });

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const roleLabel = () => {
    if (!user) return '';
    switch (user.role) {
      case 'super_admin':
        return 'Super Admin';
      case 'business_admin':
        return 'Administrador';
      case 'supervisor':
        return 'Supervisor';
      case 'employee':
        return 'Vendedor';
    }
  };

  const roleBadgeColor = () => {
    if (!user) return 'bg-foreground-200 text-foreground-700';
    switch (user.role) {
      case 'super_admin':
        return 'bg-accent-100 text-accent-700';
      case 'business_admin':
        return 'bg-primary-100 text-primary-700';
      case 'supervisor':
        return 'bg-blue-100 text-blue-700';
      case 'employee':
        return 'bg-secondary-100 text-secondary-700';
    }
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-screen z-40 flex flex-col bg-background-50 border-r border-background-200/70 transition-all duration-300 ${
        collapsed ? 'w-[72px]' : 'w-[260px]'
      }`}
    >
      <div className="flex items-center h-16 px-4 border-b border-background-200/70">
        {!collapsed && (
          <Link to={user?.role === 'employee' ? '/orders/capture' : '/dashboard'} className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0 overflow-hidden">
              {brandLogo ? <img src={brandLogo} alt="Logo del negocio" className="w-full h-full object-contain bg-white" /> : <img src="/brand/fooddash-mark-orange.png" alt="FoodDash" className="w-full h-full object-contain" />}
            </div>
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-foreground-900 truncate font-heading">
                {user?.businessName || <><span>Food</span><span className="text-primary-600">Dash</span></>}
              </h1>
              <p className="text-[10px] text-foreground-500 truncate">{user?.isB2B ? 'FoodDash Empresas' : 'FoodDash'}</p>
            </div>
          </Link>
        )}
        {collapsed && (
          <Link to={user?.role === 'employee' ? '/orders/capture' : '/dashboard'} className="mx-auto">
            <div className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center overflow-hidden">
              {brandLogo ? <img src={brandLogo} alt="Logo del negocio" className="w-full h-full object-contain bg-white" /> : <img src="/brand/fooddash-mark-orange.png" alt="FoodDash" className="w-full h-full object-contain" />}
            </div>
          </Link>
        )}
        <button
          onClick={onToggle}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer ml-auto flex-shrink-0"
        >
          <i className={`ri-arrow-left-s-line text-lg transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}></i>
        </button>
      </div>

      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const navLabel = streetSalesScope && user?.role === 'employee' && item.path === '/orders/capture' ? 'Vender' : user?.isB2B ? b2bLabels[item.path] || item.label : item.label;
            const [itemPath, itemSearch = ''] = item.path.split('?');
            const isActive = !item.external && (
              location.pathname === itemPath && (!itemSearch || location.search === `?${itemSearch}`) ||
              (!itemSearch && itemPath !== '/dashboard' && location.pathname.startsWith(itemPath))
            );
            const navigationClass = `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group cursor-pointer ${
              isActive
                ? 'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-400'
                : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
            } ${collapsed ? 'justify-center px-2' : ''}`;
            const content = <>
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-lg ${isActive ? 'text-primary-600' : 'text-foreground-400 group-hover:text-foreground-600'}`}></i>
              </div>
              {!collapsed && <span className="truncate">{navLabel}</span>}
              {!collapsed && item.external && <i className="ri-external-link-line ml-auto text-xs text-foreground-400" aria-hidden="true" />}
              {!collapsed && isActive && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0"></span>}
            </>;
            return (
              <li key={item.path}>
                {item.external ? <a href={item.path} target="_blank" rel="noopener noreferrer" className={navigationClass} title={collapsed ? item.label : `Abrir ${item.label} en una pestaña nueva`} aria-label={`${item.label}, abre un servicio externo en una pestaña nueva`}>{content}</a> : <Link to={item.path} className={navigationClass} title={collapsed ? navLabel : undefined}>{content}</Link>}
              </li>
            );
          })}
        </ul>
      </nav>

      {user?.role === 'super_admin' && (
        <div className="p-3 border-t border-background-200/70">
          <Link
            to="/admin"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
              location.pathname === '/admin'
                ? 'bg-accent-50 text-accent-700 dark:bg-accent-500/15 dark:text-accent-400'
                : 'text-foreground-500 hover:bg-background-100 hover:text-foreground-700'
            } ${collapsed ? 'justify-center px-2' : ''}`}
            title={collapsed ? 'Super Admin' : undefined}
          >
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-user-line text-lg"></i>
            </div>
            {!collapsed && <span className="truncate text-xs">Super Admin</span>}
          </Link>
        </div>
      )}

      <div className={`p-3 border-t border-background-200/70 ${collapsed ? 'text-center' : ''}`}>
        {user && (
          <>
            {!collapsed ? (
              <div className="flex items-center gap-3 px-3 py-2">
                <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-secondary-700">{user.initials}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-foreground-800 truncate">{user.name}</p>
                  <p className={`text-[10px] truncate rounded-full px-2 py-0.5 inline-block mt-0.5 ${roleBadgeColor()}`}>
                    {roleLabel()}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
                  title="Cerrar sesión"
                >
                  <i className="ri-logout-box-r-line text-sm"></i>
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center">
                  <span className="text-xs font-bold text-secondary-700">{user.initials}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer mt-0.5"
                  title="Cerrar sesión"
                >
                  <i className="ri-logout-box-r-line text-xs"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
